/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import "./Landing.scss";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRightFilled,
  PauseRegular,
  PlayRegular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";

// Other sections
import HeroSection from "../Section/HomeSection";
import EnterpriseSection from "./Enterprise/EnterpriseSection";
import HeroSectionPages from "../Section/HeroSection/HeroSectionPage";
import ManagedAgents from "../MarketingSection/MarketingSection";
import heroVideo from "../../../../../assets/video/cilpron-hero.mp4";
// ─── Temporary video for testing ───────────────────────────────


const slides = [
  {
    eyebrow: "Business Email",
    headline: "Drive growth with AI-powered email tools",
    description:
      "Deliver emails effortlessly with automation and real-time insights powered by CILPRON SendGrid.",
    tag: "Email & Messaging",
  },
  {
    eyebrow: "Sales CRM",
    headline: "Close more deals with a pipeline that runs itself",
    description:
      "Track leads, deals and follow-ups across your whole team with automation and insights built in.",
    tag: "Sales & Pipeline",
  },
  {
    eyebrow: "Accounting",
    headline: "Stay on top of your numbers in real time",
    description:
      "Invoices, expenses, VAT and cash flow — reconciled automatically while you focus on the business.",
    tag: "Finance",
  },
  {
    eyebrow: "Hospitality Suite",
    headline: "Find Hospitality solutions that scale with you",
    description:
      "Optimise guest experiences with our comprehensive hospitality management and revenue tools.",
    tag: "Property Management",
  },
  {
    eyebrow: "Developer APIs",
    headline: "Explore advanced features for modern teams",
    description:
      "Leverage cutting-edge tools for enhanced productivity and seamless enterprise integration.",
    tag: "SMS & APIs",
  },
  {
    eyebrow: "Cael AI",
    headline: "Discover Agentic Customer Service AI",
    description:
      "Power scalable, multi-tenant support with intelligent automation, real-time insights, and seamless integrations.",
    tag: "AI Agents",
  },
  {
    eyebrow: "Academic Tools",
    headline: "Unlock Academic Excellence with AI",
    description:
      "Empower students and educators with AI-driven tools for personalised learning and research assistance.",
    tag: "Education",
  },
];

const Landing: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();

  // Auto-cycle the text content
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Better play/pause control
  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (video.paused) {
        await video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    } catch (err) {
      console.error("Video play/pause failed:", err);
    }
  };

  // Keep state in sync if video ends or is controlled elsewhere
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, []);

  const current = slides[activeIndex];

  return (
    <>
      <section className="cl-hero">
        {/* ── Single full-bleed video ── */}
        <video
          ref={videoRef}
          className="cl-hero__video"
          src={heroVideo}
          autoPlay
          muted
          loop
          playsInline
          // Important: prevent native controls
          controls={false}
        />

        {/* Dark gradient overlay */}
        <div className="cl-hero__overlay" />

        {/* Breadcrumbs */}
        <nav className="cl-hero__breadcrumbs">
          <span>CILPRON</span>
          <span className="sep">›</span>
          <span>Products</span>
          <span className="sep">›</span>
          <span className="current">{current.tag}</span>
        </nav>

        {/* Glass card */}
        <div className="cl-hero__card">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div className="cl-hero__brand">
                <span className="cl-hero__brand-name">CILPRON</span>
                <span className="cl-hero__brand-product">{current.eyebrow}</span>
              </div>

              <h1 className="cl-hero__headline">{current.headline}</h1>
              <p className="cl-hero__desc">{current.description}</p>

              <div className="cl-hero__actions">
                <button
                  className="cl-btn cl-btn--primary"
                  onClick={() => navigate("auth/signup")}
                >
                  Book a demo
                  <ArrowRightFilled />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Play / Pause button - now properly working */}
        <button
          className="cl-hero__pause"
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? <PauseRegular /> : <PlayRegular />}
        </button>

        {/* Soft bottom curve */}
        <div className="cl-hero__curve" />
      </section>

      {/* Rest of page */}
      <ManagedAgents />
      <div className="hero-Section">
        <HeroSection />
      </div>
      <HeroSectionPages />
      <div className="Enterprise-Section">
        <EnterpriseSection />
      </div>
    </>
  );
};

export default Landing;